/**
 * Dialog for creating and editing calendar notes
 * Refined, minimal design with golden accents
 */

import { useState, useEffect, useCallback } from 'react'
import { Clock, FileText, Palette } from 'lucide-react'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { ColorPicker } from '@/components/shared/ColorPicker'
import { formatDateForApi, formatDateCzech } from '@/lib/calendar-utils'
import type { CalendarNote, AppointmentType } from '@/types/calendar'

interface NoteDialogProps {
	open: boolean
	onClose: () => void
	mode: 'create' | 'edit'
	note: CalendarNote | null
	date: Date
	calendarType: AppointmentType
	onCreate: (data: { date: Date; timeFrom: string; timeTo: string; text: string; type: AppointmentType; color?: string | null }) => Promise<void>
	onUpdate: (data: { id: string; timeFrom?: string; timeTo?: string; text?: string; color?: string | null }) => Promise<void>
	isLoading?: boolean
}

export function NoteDialog({
	open,
	onClose,
	mode,
	note,
	date,
	calendarType,
	onCreate,
	onUpdate,
	isLoading = false,
}: NoteDialogProps) {
	const [text, setText] = useState('')
	const [timeFrom, setTimeFrom] = useState('08:00')
	const [timeTo, setTimeTo] = useState('09:00')
	const [color, setColor] = useState<string | null>(null)

	// Initialize form when dialog opens
	useEffect(() => {
		if (!open) return

		if (mode === 'edit' && note) {
			setText(note.text)
			setTimeFrom(note.timeFrom || '08:00')
			setTimeTo(note.timeTo || '09:00')
			setColor(note.color ?? null)
		} else {
			setText('')
			setTimeFrom('08:00')
			setTimeTo('09:00')
			setColor(null)
		}
	}, [open, mode, note])

	const handleSubmit = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault()

			if (!text.trim() || !timeFrom || !timeTo) return

			try {
				if (mode === 'create') {
					await onCreate({
						date: new Date(formatDateForApi(date)),
						timeFrom,
						timeTo,
						text: text.trim(),
						type: calendarType,
						color,
					})
				} else if (note) {
					await onUpdate({
						id: note.id,
						timeFrom,
						timeTo,
						text: text.trim(),
						color,
					})
				}
				onClose()
			} catch {
				// Error handled by parent
			}
		},
		[mode, text, timeFrom, timeTo, color, date, calendarType, note, onCreate, onUpdate, onClose],
	)

	const formattedDate = formatDateCzech(date, 'EEEE d. MMMM yyyy')
	const isValid = text.trim() && timeFrom && timeTo

	return (
		<Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
			<DialogContent className="max-w-lg">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-3">
						<span className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#f5ebe0]">
							<FileText className="w-5 h-5 text-[#d7ad6c]" />
						</span>
						<div>
							<span className="block">{mode === 'create' ? 'Nová poznámka' : 'Upravit poznámku'}</span>
							<span className="block text-sm font-normal text-[#8c8275] capitalize mt-0.5">{formattedDate}</span>
						</div>
					</DialogTitle>
				</DialogHeader>

				<form onSubmit={handleSubmit} className="space-y-6 mt-2">
					{/* Time range */}
					<div className="space-y-3">
						<Label className="flex items-center gap-2 text-[#5c5142] font-medium">
							<Clock className="w-4 h-4 text-[#8c8275]" />
							Časový rozsah
						</Label>
						<div className="flex items-center gap-3">
							<div className="flex-1">
								<Input
									type="time"
									value={timeFrom}
									onChange={(e) => setTimeFrom(e.target.value)}
									required
									className="h-12 text-base text-[#3d3428] bg-[#faf8f5] border border-[#e8e4df] rounded-xl focus:border-[#d7ad6c] focus:outline-none transition-colors"
								/>
							</div>
							<span className="text-[#8c8275] font-medium">—</span>
							<div className="flex-1">
								<Input
									type="time"
									value={timeTo}
									onChange={(e) => setTimeTo(e.target.value)}
									required
									className="h-12 text-base text-[#3d3428] bg-[#faf8f5] border border-[#e8e4df] rounded-xl focus:border-[#d7ad6c] focus:outline-none transition-colors"
								/>
							</div>
						</div>
					</div>

					{/* Text */}
					<div className="space-y-3">
						<Label htmlFor="note-text" className="flex items-center gap-2 text-[#5c5142] font-medium">
							<FileText className="w-4 h-4 text-[#8c8275]" />
							Text poznámky
						</Label>
						<Textarea
							id="note-text"
							placeholder="Napište poznámku..."
							value={text}
							onChange={(e) => setText(e.target.value)}
							rows={4}
							autoFocus
							className="text-base text-[#3d3428] bg-[#faf8f5] border border-[#e8e4df] rounded-xl focus:border-[#d7ad6c] focus:outline-none transition-colors resize-none"
						/>
					</div>

					{/* Color */}
					<div className="space-y-3">
						<Label className="flex items-center gap-2 text-[#5c5142] font-medium">
							<Palette className="w-4 h-4 text-[#8c8275]" />
							Barva
						</Label>
						<ColorPicker
							value={color ?? ''}
							onChange={(newColor) => setColor(newColor)}
						/>
					</div>

					{/* Actions */}
					<div className="flex justify-end gap-3 pt-4 border-t border-[#e8e4df]">
						<Button
							type="button"
							variant="outline"
							onClick={onClose}
							className="h-11 px-6 rounded-xl bg-white border border-[#e8e4df] text-[#5c5142] hover:bg-[#f5ebe0] hover:border-[#d7ad6c] transition-all"
						>
							Zrušit
						</Button>
						<Button
							type="submit"
							disabled={isLoading || !isValid}
							className="h-11 px-6 rounded-xl bg-[#d7ad6c] text-white hover:bg-[#c49b5a] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
						>
							{isLoading
								? 'Ukládání...'
								: mode === 'create'
									? 'Vytvořit poznámku'
									: 'Uložit změny'}
						</Button>
					</div>
				</form>
			</DialogContent>
		</Dialog>
	)
}
